/**
 * 极简紫韵模版 - 登录页
 * 
 * 设计理念：左侧登录卡片 + 右侧动画几何图形背景
 * 与所有现有登录页（居中卡片/品牌分栏/全屏背景）完全不同：
 * - 左：登录卡片（非居中，靠左放置）
 * - 右：动态浮动几何图形 + 品牌文案
 * - 紫色渐变动画光晕背景
 */

import { useState, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { useAuth } from "@/hooks/useAuth";

export default function PurpleLoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { siteName: contextSiteName } = useSite();
  const { siteConfig } = useSiteConfig();

  const [formData, setFormData] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const siteName = contextSiteName || siteConfig?.site_name || siteConfig?.name || "";
  const logo = siteConfig?.logo || "";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!formData.username.trim() || !formData.password.trim()) {
      setError("请填写用户名和密码");
      return;
    }
    setSubmitting(true);
    try {
      await login(formData.username, formData.password);
      navigate("/");
    } catch (err: any) {
      setError(err?.message || "登录失败，请重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden bg-white">
      {/* 背景动态光晕 */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/3 w-96 h-96 rounded-full bg-purple-400/10 blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-violet-400/10 blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 rounded-full bg-fuchsia-400/5 blur-3xl" />
      </div>

      {/* ======== 左侧登录卡片 ======== */}
      <div className="w-full lg:w-[45%] flex items-center justify-center p-6 sm:p-10 relative z-10">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-2xl shadow-purple-500/10 border border-purple-100/50 p-8 sm:p-10">
            {/* Logo */}
            <div className="mb-6">
              {logo ? (
                <img src={logo} alt={siteName} className="h-10" />
              ) : (
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-400 via-violet-500 to-fuchsia-500 flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-purple-500/20">
                  {siteName ? siteName.charAt(0).toUpperCase() : 'P'}
                </div>
              )}
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-1">欢迎回来</h2>
            <p className="text-sm text-gray-400 mb-8">
              还没有账号？{" "}
              <Link to="/register" className="text-purple-600 hover:text-purple-700 font-semibold">
                立即注册
              </Link>
            </p>

            {error && (
              <div className="mb-5 p-3.5 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 flex items-center gap-2">
                <i className="ri-error-warning-line" />{error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">用户名 / 手机号</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-user-line text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="请输入用户名"
                    className="w-full pl-11 pr-4 py-3.5 bg-purple-50/30 border border-purple-100 rounded-2xl text-gray-700 placeholder-gray-400 focus:outline-none focus:border-purple-400 focus:ring-4 focus:ring-purple-500/10 transition-all text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">密码</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-lock-line text-gray-400" />
                  </div>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="请输入密码"
                    className="w-full pl-11 pr-4 py-3.5 bg-purple-50/30 border border-purple-100 rounded-2xl text-gray-700 placeholder-gray-400 focus:outline-none focus:border-purple-400 focus:ring-4 focus:ring-purple-500/10 transition-all text-sm"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-gray-500 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded accent-purple-500" />
                  记住我
                </label>
                <Link to="/forgot-password" className="text-purple-600 hover:text-purple-700 font-medium">
                  忘记密码？
                </Link>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 bg-gradient-to-r from-purple-600 via-violet-600 to-fuchsia-600 text-white font-bold rounded-2xl hover:from-purple-700 hover:via-violet-700 hover:to-fuchsia-700 transition-all shadow-lg shadow-purple-500/25 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />登录中...
                  </span>
                ) : "立即登录"}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-gray-400 hover:text-gray-500 inline-flex items-center gap-1">
                <i className="ri-arrow-left-line" />返回首页
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ======== 右侧动画图形区 ======== */}
      <div className="hidden lg:flex flex-1 relative items-center justify-center bg-gradient-to-br from-purple-50 via-violet-50 to-fuchsia-50 overflow-hidden">
        {/* 网格背景 */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'radial-gradient(circle, #7c3aed 1px, transparent 1px)',
            backgroundSize: '30px 30px',
          }}
        />

        {/* 浮动几何图形 */}
        <div className="relative w-80 h-80">
          {/* 大圆 */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full bg-gradient-to-br from-purple-400 to-violet-500 opacity-80 shadow-2xl shadow-purple-300/40 animate-bounce" style={{ animationDuration: '4s' }} />

          {/* 小三角 */}
          <div className="absolute top-20 left-0 w-0 h-0 border-l-[30px] border-r-[30px] border-b-[52px] border-l-transparent border-r-transparent border-b-fuchsia-400/70 animate-bounce" style={{ animationDuration: '3s', animationDelay: '0.5s' }} />

          {/* 中方块 */}
          <div className="absolute bottom-10 right-4 w-20 h-20 rounded-2xl bg-indigo-400/80 shadow-xl shadow-indigo-300/40 rotate-12 animate-bounce" style={{ animationDuration: '5s', animationDelay: '1s' }} />

          {/* 小圆点 */}
          <div className="absolute top-10 right-10 w-8 h-8 rounded-full bg-purple-300/60 animate-pulse" />
          <div className="absolute bottom-20 left-10 w-5 h-5 rounded-full bg-violet-300/60 animate-pulse" style={{ animationDelay: '1.5s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-fuchsia-400/50 animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        {/* 品牌文案 */}
        <div className="absolute bottom-16 inset-x-0 text-center px-8">
          {siteName ? (
            <>
              <h3 className="text-2xl font-extrabold text-gray-800 mb-2">
                <span className="bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">{siteName}</span>
              </h3>
              <p className="text-sm text-gray-500">登录即可管理您的分类信息</p>
            </>
          ) : (
            <>
              <h3 className="text-2xl font-extrabold text-gray-800 mb-2">
                <span className="bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">分类信息平台</span>
              </h3>
              <p className="text-sm text-gray-500">一键发布，精准搜索</p>
            </>
          )}

          {/* 特性徽标 */}
          <div className="flex items-center justify-center gap-3 mt-6">
            {[
              { icon: 'ri-shield-check-line', label: '安全可靠' },
              { icon: 'ri-flashlight-line', label: '极速发布' },
              { icon: 'ri-vip-crown-line', label: 'VIP特权' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/80 backdrop-blur-sm rounded-full border border-purple-100 text-xs text-gray-600">
                <i className={`${item.icon} text-purple-400`} />
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
